import { createContext, useContext } from 'react'

export interface ImpersonationInfo {
  impersonating: boolean
  originalAdmin?: string
  targetUser?: string
}

export interface UserFeatures {
  templatesVisibleToUsers: boolean
  templatesVisibleToMe: boolean
}

export interface UserRole {
  name: string
  description?: string | null
  isSystem: boolean
}

export interface UserPermissionDetail {
  key: string
  description?: string | null
  category: string
}

export interface UserContextType {
  email: string
  name: string
  firstName?: string
  lastName?: string
  services?: string[]
  isAdmin: boolean
  /**
   * Effective RBAC permission keys (e.g. "users.read", "workspaces.write").
   * Empty for admins — `isAdmin` already implies every permission, so callers
   * should use `can()` rather than reading this array directly.
   */
  permissions: string[]
  /** Roles assigned to the user (read-only, for the profile view). */
  roles: UserRole[]
  /** Effective permissions enriched with description + category for display. */
  permissionDetails: UserPermissionDetail[]
  impersonation?: ImpersonationInfo
  features: UserFeatures
}

export const UserContext = createContext<UserContextType | null>(null)

export function useUser() {
  const ctx = useContext(UserContext)
  if (!ctx) throw new Error('useUser must be used within UserProvider')
  return ctx
}

/**
 * Permission-gate hook. `can("users.read")` is true when the user is an admin
 * (admins implicitly hold every permission, matching the backend
 * require_permission order) OR explicitly holds the key.
 *
 * Always keeps `isAdmin` as a master override so an admin can never be locked
 * out of a screen by a missing catalog key.
 */
export function useCan(): (permission: string) => boolean {
  const ctx = useContext(UserContext)
  if (!ctx) throw new Error('useCan must be used within UserProvider')
  return (permission: string) =>
    ctx.isAdmin || ctx.permissions.includes(permission)
}
